/**
 * Where a juhuklient came from, as something you can follow.
 *
 * The board used to print `lead.source` raw, which meant the scanner's own
 * leads carried a badge reading "site-scanner" and nothing else. Two problems
 * in one word: it is not a sentence in either interface language, and it is a
 * dead end - the report that decided this company was worth calling, with the
 * platform, the versions and the findings on it, was one page away and the
 * record said nothing about how to get there.
 *
 * So the badge is translated, and when the source has a destination it IS the
 * link. Two do: a scanner lead points at its scan report, a register lead at
 * the company's entry. The rest stay plain - a form submission and a voicemail
 * are events, and this record is already the only page they have.
 *
 * The scan is addressed by the lead's website rather than by an id, because
 * that is the whole key the scanner uses: `parseWebUrl` is the same parser the
 * register's own links go through, so a value typed without a scheme does not
 * turn into a relative path (see `lib/webUrl.ts`).
 */
import { Link } from "react-router-dom";
import { Building2, Radar } from "lucide-react";
import { Badge, cn } from "@wa/ui";
import { leadSourceLabel, leadSourceRoute } from "../../../lib/leadSource";
import { parseWebUrl } from "../../../lib/webUrl";
import type { Lead } from "../../../lib/clients";
import { useT } from "../../../i18n/I18nProvider";

type T = ReturnType<typeof useT>;

/** The scanner keys everything by bare host, which is what `label` holds. */
export const scanDomain = (website: string | null): string | null => parseWebUrl(website)?.label ?? null;

export function LeadSource({ lead, t, className }: { lead: Lead; t: T; className?: string }) {
  if (!lead.source) return null;

  const label = leadSourceLabel(lead.source, t);
  const to = leadSourceRoute(lead.source, {
    regCode: lead.regCode,
    scanDomain: scanDomain(lead.website),
  });

  if (!to) {
    return (
      <Badge variant="secondary" className={cn("text-[10px]", className)} title={t("leads.srcTitle", { source: label })}>
        {label}
      </Badge>
    );
  }

  const scanner = lead.source === "site-scanner";
  const Icon = scanner ? Radar : Building2;

  return (
    <Link
      to={to}
      title={t(scanner ? "leads.srcOpenScan" : "leads.srcOpenRegister")}
      className={cn(
        "inline-flex items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary",
        className,
      )}
    >
      <Icon className="size-2.5 shrink-0" />
      {label}
    </Link>
  );
}
